import { cx } from "../../lib/cx.js";
import { IconButton } from "../IconButton/IconButton.js";
/* empty css                 */
import { useId, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/SortableList/SortableList.tsx
/**
* An ordered list whose items can be moved: by dragging a row's handle, by picking the
* handle up with Space and moving it with the arrow keys, or with each row's Move up and
* Move down buttons. Every move is announced ("Amount moved to position 3 of 7"), and
* focus stays with the moved item.
*/
function SortableList({ items, getKey, getLabel, renderItem, onReorder, label, disabled = false, className }) {
	const id = useId();
	const listRef = useRef(null);
	const [mode, setMode] = useState({ kind: "idle" });
	const [draggingKey, setDraggingKey] = useState(null);
	const [announcement, setAnnouncement] = useState("");
	const count = items.length;
	const position = (index) => `position ${index + 1} of ${count}`;
	/** After the move renders, puts focus back on the item's control (DOM moves drop it). */
	const refocus = (key, control) => {
		requestAnimationFrame(() => {
			const row = listRef.current?.querySelector(`[data-key="${CSS.escape(key)}"]`);
			const wanted = row?.querySelector(`[data-control="${control}"]`);
			(wanted && !wanted.disabled ? wanted : row?.querySelector("[data-control=\"handle\"]"))?.focus();
		});
	};
	const move = (from, to, control) => {
		if (to < 0 || to >= count || to === from) return;
		const item = items[from];
		onReorder(from, to);
		setAnnouncement(`${getLabel(item)} moved to ${position(to)}`);
		refocus(getKey(item), control);
	};
	const onHandleKeyDown = (event, index) => {
		if (disabled) return;
		const item = items[index];
		const key = getKey(item);
		const picked = mode.kind === "picked" && mode.key === key;
		if (event.key === " " || event.key === "Enter") {
			event.preventDefault();
			if (picked) {
				setMode({ kind: "idle" });
				setAnnouncement(`${getLabel(item)} dropped at ${position(index)}`);
			} else {
				setMode({
					kind: "picked",
					key,
					origin: index
				});
				setAnnouncement(`${getLabel(item)} picked up, ${position(index)}. Use the arrow keys to move it, Space to drop it, Escape to cancel.`);
			}
			return;
		}
		if (!picked) return;
		if (event.key === "ArrowUp" || event.key === "ArrowDown") {
			event.preventDefault();
			move(index, index + (event.key === "ArrowUp" ? -1 : 1), "handle");
		} else if (event.key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			setMode({ kind: "idle" });
			if (index !== mode.origin) onReorder(index, mode.origin);
			setAnnouncement(`Move cancelled. ${getLabel(item)} is back at ${position(mode.origin)}`);
			refocus(key, "handle");
		}
	};
	const drag = useRef(null);
	const onPointerDown = (event, index) => {
		if (disabled || event.button !== 0) return;
		event.preventDefault();
		try {
			event.currentTarget.setPointerCapture?.(event.pointerId);
		} catch {}
		const key = getKey(items[index]);
		drag.current = {
			key,
			index
		};
		setDraggingKey(key);
	};
	const onPointerMove = (event) => {
		const current = drag.current;
		if (!current || !listRef.current) return;
		const rows = Array.from(listRef.current.children);
		let target = current.index;
		rows.forEach((row, i) => {
			const { top, height } = row.getBoundingClientRect();
			const middle = top + height / 2;
			if (i < current.index && event.clientY < middle) target = Math.min(target, i);
			if (i > current.index && event.clientY > middle) target = Math.max(target, i);
		});
		if (target !== current.index) {
			onReorder(current.index, target);
			current.index = target;
		}
	};
	const endDrag = (event) => {
		const current = drag.current;
		if (!current) return;
		drag.current = null;
		setDraggingKey(null);
		if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
		const item = items.find((candidate) => getKey(candidate) === current.key);
		if (item) setAnnouncement(`${getLabel(item)} moved to ${position(current.index)}`);
	};
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [
		/* @__PURE__ */ jsx("p", {
			id: `${id}help`,
			className: "ds-sortable__sr",
			children: "To move an item, press Space on its handle, then the arrow keys; or use its Move up and Move down buttons."
		}),
		/* @__PURE__ */ jsx("ol", {
			ref: listRef,
			"aria-label": label,
			className: cx("ds-sortable", disabled && "ds-sortable--disabled", className),
			children: items.map((item, index) => {
				const key = getKey(item);
				const name = getLabel(item);
				const picked = mode.kind === "picked" && mode.key === key;
				return /* @__PURE__ */ jsxs("li", {
					"data-key": key,
					className: cx("ds-sortable__item", picked && "ds-sortable__item--picked", draggingKey === key && "ds-sortable__item--dragging"),
					children: [
						/* @__PURE__ */ jsx(IconButton, {
							icon: "grip",
							size: "sm",
							"data-control": "handle",
							className: "ds-sortable__handle",
							"aria-label": `Reorder ${name}`,
							"aria-describedby": `${id}help`,
							"aria-pressed": picked,
							disabled,
							onKeyDown: (event) => onHandleKeyDown(event, index),
							onBlur: (event) => {
								if (picked && event.relatedTarget) setMode({ kind: "idle" });
							},
							onPointerDown: (event) => onPointerDown(event, index),
							onPointerMove,
							onPointerUp: endDrag,
							onPointerCancel: endDrag
						}),
						/* @__PURE__ */ jsx("div", {
							className: "ds-sortable__content",
							children: renderItem(item, index)
						}),
						/* @__PURE__ */ jsxs("span", {
							className: "ds-sortable__moves",
							children: [/* @__PURE__ */ jsx(IconButton, {
								icon: "chevron-up",
								size: "sm",
								"data-control": "up",
								"aria-label": `Move ${name} up`,
								disabled: disabled || index === 0,
								onClick: () => move(index, index - 1, "up")
							}), /* @__PURE__ */ jsx(IconButton, {
								icon: "chevron-down",
								size: "sm",
								"data-control": "down",
								"aria-label": `Move ${name} down`,
								disabled: disabled || index === count - 1,
								onClick: () => move(index, index + 1, "down")
							})]
						})
					]
				}, key);
			})
		}),
		/* @__PURE__ */ jsx("span", {
			className: "ds-sortable__sr",
			role: "status",
			children: announcement
		})
	] });
}
//#endregion
export { SortableList };

//# sourceMappingURL=SortableList.js.map